import { useEffect, useState } from "react";
import api, { downloadPdf } from "@/lib/api";
import AddToFolderModal from "@/components/AddToFolderModal";
import { X, FolderSimple, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ContactEditor({ c, reload }) {
  const [email, setEmail] = useState(c.email || "");
  const [telephone, setTelephone] = useState(c.telephone || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/companies/${c.id}`, { email, telephone }); await reload(); toast.success("Coordonnées enregistrées"); }
    catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-border p-5">
      <div className="overline text-muted-foreground mb-3">Coordonnées de contact</div>
      <label className="overline text-muted-foreground">Email</label>
      <input data-testid="contact-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@cabinet.fr"
        className="w-full mt-1 mb-3 px-3 py-2 text-sm border border-border outline-none focus:border-primary font-mono" />
      <label className="overline text-muted-foreground">Téléphone</label>
      <input data-testid="contact-phone-input" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="+33 6 12 34 56 78"
        className="w-full mt-1 mb-3 px-3 py-2 text-sm border border-border outline-none focus:border-primary font-mono" />
      <button onClick={save} disabled={saving} data-testid="contact-save-btn"
        className="w-full py-2 bg-secondary text-sm font-semibold hover:bg-border disabled:opacity-60">{saving ? "…" : "Enregistrer"}</button>
    </div>
  );
}

export function TagsFolders({ c, reload }) {
  const [tags, setTags] = useState(c.tags || []);
  const [input, setInput] = useState("");
  const [folders, setFolders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const loadFolders = () => api.get(`/companies/${c.id}/folders`).then((r) => setFolders(r.data));
  useEffect(() => { setTags(c.tags || []); loadFolders(); }, [c.id, c.tags]);
  const saveTags = async (next) => {
    setTags(next);
    try { await api.patch(`/companies/${c.id}`, { tags: next }); reload(); }
    catch { toast.error("Erreur tags"); }
  };
  const addTag = () => {
    const t = input.trim();
    if (!t || tags.includes(t)) { setInput(""); return; }
    saveTags([...tags, t]); setInput("");
  };
  const removeTag = (t) => saveTags(tags.filter((x) => x !== t));
  const removeFolder = async (fid) => {
    try { await api.delete(`/folders/${fid}/companies/${c.id}`); loadFolders(); }
    catch { toast.error("Erreur"); }
  };
  return (
    <div className="bg-white border border-border p-5" data-testid="tags-folders-block">
      <div className="overline text-muted-foreground mb-3">Typologie & Dossiers</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.length === 0 && <span className="text-xs text-muted-foreground">Aucun tag</span>}
        {tags.map((t) => (
          <span key={t} data-testid={`tag-${t}`} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
            {t}
            <button onClick={() => removeTag(t)} className="hover:text-destructive"><X size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2 mb-4">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()}
          data-testid="tag-input" placeholder="Ajouter un tag (ex : IARD)…"
          className="flex-1 px-2 py-1.5 text-sm border border-border outline-none focus:border-primary" />
        <button onClick={addTag} data-testid="add-tag-btn" className="px-3 py-1.5 text-sm font-semibold bg-secondary hover:bg-border">+</button>
      </div>
      <div className="overline text-muted-foreground mb-1">Dossiers</div>
      <div className="space-y-1 mb-2">
        {folders.length === 0 && <span className="text-xs text-muted-foreground">Dans aucun dossier</span>}
        {folders.map((f) => (
          <div key={f.id} data-testid={`company-folder-${f.id}`} className="flex items-center gap-2 text-sm">
            <FolderSimple size={14} style={{ color: f.color || "#002FA7" }} weight="fill" />
            <span className="flex-1 truncate">{f.name}</span>
            <button onClick={() => removeFolder(f.id)} className="text-muted-foreground hover:text-destructive"><X size={13} /></button>
          </div>
        ))}
      </div>
      <button data-testid="add-to-folder-btn" onClick={() => setShowModal(true)}
        className="w-full px-3 py-2 text-sm font-semibold border border-primary text-primary hover:bg-accent">
        + Ajouter à un dossier
      </button>
      {showModal && <AddToFolderModal companyIds={[c.id]} onClose={() => setShowModal(false)} onDone={loadFolders} />}
    </div>
  );
}

export function GeneratedDocs({ c }) {
  const [busy, setBusy] = useState("");
  const gen = async (kind) => {
    setBusy(kind);
    try {
      const file = `${kind.toUpperCase()}_${(c.nom || "cabinet").replace(/\s+/g, "_")}.pdf`;
      await downloadPdf(`/companies/${c.id}/${kind}.pdf`, file);
    } catch { toast.error("Erreur génération PDF"); } finally { setBusy(""); }
  };
  return (
    <div className="bg-white border border-border p-5" data-testid="generated-docs-block">
      <div className="overline text-muted-foreground mb-3">Documents (PDF serveur)</div>
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="gen-loi-btn" onClick={() => gen("loi")} disabled={busy}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold border border-primary text-primary hover:bg-accent disabled:opacity-50">
          <FilePdf size={15} /> {busy === "loi" ? "…" : "LOI"}
        </button>
        <button data-testid="gen-nda-btn" onClick={() => gen("nda")} disabled={busy}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold border border-primary text-primary hover:bg-accent disabled:opacity-50">
          <FilePdf size={15} /> {busy === "nda" ? "…" : "NDA"}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Modèles standards FR pré-remplis (valorisation, coordonnées).</p>
    </div>
  );
}
